import { FC, Fragment, ReactNode, useEffect, useState, useMemo } from 'react';
import { isSafari } from 'react-device-detect';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Box, Button, IconButton, Stack, SwipeableDrawer, Typography } from '@mui/material';

import Logo from 'components/common/svg/logo';
import AccountIcon from 'components/common/icons/Account';
import CloseIcon from 'components/common/icons/Close';
import LiveCasinoIcon from 'components/common/icons/LiveCasino';
import MenuIcon from 'components/common/icons/Menu';
import RealWalletIcon from 'components/common/icons/RealWallet';
import CasinoIcon from 'components/common/icons/SlotsCasino';
import SportIcon from 'components/common/icons/Sport';
import SportLiveIcon from 'components/common/icons/SportLive';

import Notifications from 'components/sections/Navigation/UserSection/Notifications';
import UserProfile from 'components/sections/Navigation/UserSection/UserProfile';

import useCategories from 'store/categories';
import useCurrentUser from 'store/currentUser';
import useSelectedTheme from 'store/theme';
import useDialog from 'store/dialog';
import useSettings, { IWebsiteConfigs } from 'store/settings';
import useWebsiteSports from 'store/websiteSports';

import useTranslates from 'utils/translate';

import UserWallet from '../UserSection/Wallet';

import { MOBILE_HEADER_ID, MOBILE_NAVBAR_ID } from '../constants';
import MobileMenu from './Menu';
import sx from './styles';
import ApplicationDialog from 'components/sections/ApplicationDialog';
import { hasLiveType, hasPrematchType } from 'utils/predicates';
import { useSportQueries } from 'hooks/useSportLinks';
import HomeLogo from 'components/common/svg/HomeLogo';
import useScreen from 'hooks/useScreen';
import useRealBalance from 'store/realBalance';
import RefreshButton from 'components/common/Actions/RefreshButton';
import Sports from 'components/common/icons/Sports';


interface IMobileNavigationProps {
  footerNavigation?: ReactNode;
  headerVisible?: boolean;
}

const MobileNavigation: FC<IMobileNavigationProps> = ({ footerNavigation, headerVisible = true }) => {
  const { translate } = useTranslates();
  const navigate = useNavigate();
  const [{ isDark }] = useSelectedTheme();
  const [settings] = useSettings();
  const [, realBalanceActions] = useRealBalance();
  const { isMobile } = useScreen();
  const [categories] = useCategories();
  const [, dialogActions] = useDialog();
  const [currentUser] = useCurrentUser();
  const [websiteSports] = useWebsiteSports();
  const { getLiveQuery, getSportQuery } = useSportQueries();
  const [
    {
      data: { headerBGColor, headerFontColor, headerBGColorDark, headerFontColorDark, meta = {} as IWebsiteConfigs },
    },
  ] = useSettings();
  const { pathname, search } = useLocation();

  const [opened, setOpened] = useState(false);

  const href = `${pathname}${search}`;

  const onMenuClose = () => setOpened(false);

  const toggleOpened = () => setOpened((prev) => !prev);


  useEffect(() => {
    setOpened(false);
  }, [href]);

  const hideNavBarItems = ['/game/', '/sport'];

  const isHomePagePathname = pathname === '/';

  const showNavBar =
    !hideNavBarItems.some((hideNavBarItem) => pathname.includes(hideNavBarItem)) &&
    !(!meta?.isHomepageActive && isHomePagePathname);

  const { preferredSportIds = [] } = meta;
  const possiblyPreferredWebsiteSports = preferredSportIds.length
    ? preferredSportIds
        .map((preferredSportId) => websiteSports.data.find(({ id }) => id === preferredSportId))
        .filter(Boolean)
    : websiteSports.data;

  const filteredWebsiteSports =
    preferredSportIds.length && possiblyPreferredWebsiteSports.length
      ? possiblyPreferredWebsiteSports
      : websiteSports.data;

  const menuItemClick = (event, isLoginRequired) => {
    if (!currentUser?.data?.id && isLoginRequired) {
      event.stopPropagation();
      event.preventDefault();
      dialogActions.set('signIn');
    }
  };
  const showMenuIcons = useMemo(() => {
    return settings?.data?.meta?.showMenuIcons;
  }, [settings?.data?.meta?.showMenuIcons]);

  const isAppHeaderWithoutUser = useMemo(() => {
    return meta?.mobileHeader === 'app' && !currentUser?.data?.id;
  }, [meta?.mobileHeader, currentUser?.data?.id]);

  const isOverlapMenuMobileView = isAppHeaderWithoutUser;
  const showUnAuthAppVersion = isAppHeaderWithoutUser;

  const headerStyles = useMemo(() => {
    if (isDark && (headerBGColorDark || headerFontColorDark)) {
      return {
        color: headerFontColorDark || 'undefined',
        bgcolor: headerBGColorDark || 'undefined',
      };
    }

    if (!isDark && (headerBGColor || headerFontColor)) {
      return {
        color: headerFontColor || 'undefined',
        bgcolor: headerBGColor || 'undefined',
      };
    }

    return {};
  }, [headerBGColor, headerFontColor, isDark, headerBGColorDark, headerFontColorDark]);

  const shouldShowNavBar = useMemo(() => {
    const isAuthPage = pathname === '/sign-up' || pathname === '/sign-in';
    const isRestrictedMobilePage =
        isMobile && (pathname === '/deposit' ||
            pathname === '/withdraw' ||
            pathname === '/wallet-history' ||
            pathname === '/verification' ||
            pathname === '/player/profile' ||
            pathname === '/player/reset-password' ||
            pathname === '/2fa'
        );

    return showNavBar && !isAuthPage && !isRestrictedMobilePage;
  }, [pathname, isMobile, showNavBar]);
  return (
    <>
      {meta.appLinkPosition !== 'bottom' && <ApplicationDialog forceHide={!isHomePagePathname || opened} />}
      {headerVisible && (
        <Box sx={sx.head({ opened, isSafari, isApp: showUnAuthAppVersion, isOverlapMenuMobileView, headerStyles })} id={MOBILE_HEADER_ID}>
          <Link to="/">
            <Logo useInlineBounds onClick={onMenuClose} />
          </Link>
          <Stack
            direction="row"
            gap={0.5}
            alignItems="center"
            sx={{
              ...(showUnAuthAppVersion
                ? {
                    width: '100%',
                  }
                : {}),
            }}
          >
            {currentUser?.data?.id ? (
              <>
                {meta.showDeposit && meta.showDepositIconOnMobileHeader && (
                  <IconButton color="success" onClick={() => navigate(`/deposit`)}>
                    <RealWalletIcon  />
                  </IconButton>
                )}
                <UserWallet showOnlyBalance isWalletInAccount={meta.isWalletInAccount} />
                {meta?.showBalanceUpdate && (<RefreshButton onClick={() => realBalanceActions.read()} fontSize={18} color="inherit" animationEnabled={true} />)}
                <Notifications />
                <UserProfile user={currentUser?.data} isWalletInAccount={meta.isWalletInAccount}>
                  <IconButton color="inherit">
                    <AccountIcon />
                  </IconButton>
                </UserProfile>
              </>
            ) : (
              <>
                <Button
                  sx={{
                    height: showUnAuthAppVersion ? 40 : 30,
                    padding: '12px',
                    color: meta.customColors?.signIn || 'text.main',
                    backgroundColor: meta.customColors?.signInBg || 'primary.main',
                    ...(showUnAuthAppVersion ? { flexGrow: 1 } : {}),
                  }}
                  variant="contained"
                  onClick={() => navigate(`/sign-in${search}`)}
                  data-testid="sign-in-button"
                >
                  {translate('sign_in')}
                </Button>
                {meta.showSignup && (
                  <Button
                    sx={{
                      height: showUnAuthAppVersion ? 40 : 30,
                      padding: '12px',
                      color: meta.customColors?.signIn || 'text.main',
                      backgroundColor: meta.customColors?.signInBg || 'primary.main',
                      ...(showUnAuthAppVersion ? { flexGrow: 1 } : {}),
                    }}
                    variant="contained"
                    onClick={() => navigate(`/sign-up${search}`)}
                    data-testid="sign-up-button"
                  >
                    {translate('sign_up')}
                  </Button>
                )}
              </>
            )}
          </Stack>
        </Box>
      )}
      {shouldShowNavBar && (
        <Stack sx={sx.menuItems} direction="row" gap="20px">
          {meta?.isHomepageActive && (
            <Button component={Link} to="/" sx={sx.button({ selected: pathname === '/', showIcon:showMenuIcons })}>
              {showMenuIcons && <HomeLogo />}
              {translate('home')}
            </Button>
          )}

          {websiteSports.data.filter(hasPrematchType).map((websiteSport, index) => {
            const path = !meta?.isHomepageActive && !index ? '/' : `/sport/${websiteSport.id}`;

            return (
              <Button
                component={Link}
                to={`${path}${getSportQuery(websiteSport)}`}
                key={path}
                sx={sx.button({ selected: pathname === path, showIcon: showMenuIcons })}
              >
                {showMenuIcons &&
                    ((websiteSport.id === 9 ||  websiteSport.id === 5) ? (
                        <Sports/>
                    ) : (
                        <SportIcon />
                    ))}
                {translate(`sport_${websiteSport.id}`)}
              </Button>
            );
          })}
          {meta.showGamesInTopNavigation && (
            <Button key="/games" component={Link} to="/games" sx={sx.button({ selected: pathname === '/games', showIcon: showMenuIcons })}>
              {showMenuIcons && <CasinoIcon/>}
              {translate('games')}
            </Button>
          )}

          {categories?.data?.map((item) => {
            const isDirectGameId = !Number.isNaN(Number(item.uri));
            const path = isDirectGameId ? `/game/${item.uri}` : `/games/${item.uri}`;

            return (
              <Button
                to={path}
                key={item.key}
                component={(props) => <Link {...props} onClick={(event) => menuItemClick(event, isDirectGameId)} />}
                sx={sx.button({ selected: pathname === path, showIcon: showMenuIcons })}
              >
                {showMenuIcons &&
                    <Box
                        sx={{
                          width: 24,
                          height: 24,
                          backgroundSize: 'cover',
                          backgroundImage: `url(${item?.[isDark ? 'iconDark' : 'icon']})`,
                        }}
                    />
                }
                {translate(item.name)}
              </Button>
            );
          })}
        </Stack>
      )}

      <SwipeableDrawer
        anchor="right"
        open={opened}
        sx={sx.drawer}
        PaperProps={{
          sx: sx.drawerPaper,
        }}
        onOpen={() => setOpened(true)}
        onClose={() => setOpened(false)}
      >
        <MobileMenu onMenuClose={onMenuClose} />
      </SwipeableDrawer>
      {meta.appLinkPosition === 'bottom' && isHomePagePathname && !opened && (
        <Box
          sx={{
            position: 'fixed',
            bottom: '60px',
            left: 0,
            width: '100%',
            zIndex: 1300,
          }}
        >
          <ApplicationDialog forceHide={!isHomePagePathname || opened} />
        </Box>
      )}
      {(footerNavigation || showNavBar) && (
        <Stack
          id={MOBILE_NAVBAR_ID}
          sx={sx.navBar}
          direction="row"
          gap={meta?.showNavbarHome ? 0 : 1}
          display="flex"
          style={{
            overflowX: 'auto',
            overflowY: 'hidden',
          }}
        >
          {footerNavigation || (
            <>
              {meta?.showNavbarHome && (
                <Link to="/">
                  <Box sx={sx.navButton({ selected: pathname === '/', isApp: meta?.showNavbarHome })}>
                    <HomeLogo />
                    <Typography>{translate('home')}</Typography>
                  </Box>
                </Link>
              )}
              {filteredWebsiteSports.map((websiteSport, index) => {
                const path = !meta?.isHomepageActive && !index ? '/' : `/sport/${websiteSport.id}`;

                return (
                  <Fragment key={index}>
                    {!preferredSportIds.length && hasLiveType(websiteSport) && (
                      <Link to={`${path}${getLiveQuery(websiteSport)}`}>
                        <Box sx={sx.navButton({ selected: false, isApp: meta?.showNavbarHome })}>
                          <SportLiveIcon sx={{ fontSize: 24 }} />
                          <Typography>{translate(`live_${websiteSport.id}`)}</Typography>
                        </Box>
                      </Link>
                    )}
                    {hasPrematchType(websiteSport) && (
                      <Link to={`${path}${getSportQuery(websiteSport)}`}>
                        <Box sx={sx.navButton({ selected: false, isApp: meta?.showNavbarHome })}>
                          {(websiteSport.id === 9 ||  websiteSport.id === 5)? (<Sports sx={{ fontSize: 24 }} />):(<SportIcon sx={{ fontSize: 24 }} />)}
                          <Typography>{translate(`sport_${websiteSport.id}`)}</Typography>
                        </Box>
                      </Link>
                    )}
                  </Fragment>
                );
              })}

              <Link to="/games/slots">
                <Box sx={sx.navButton({ selected: pathname === '/games/slots', isApp: meta?.showNavbarHome })}>
                  <CasinoIcon />
                  <Typography>{translate('casino')}</Typography>
                </Box>
              </Link>
              <Link to="/games/live_casino">
                <Box sx={sx.navButton({ selected: pathname === '/games/live_casino', isApp: meta?.showNavbarHome })}>
                  <LiveCasinoIcon />
                  <Typography>{translate('live_casino')}</Typography>
                </Box>
              </Link>
            </>
          )}
          <Box sx={sx.navButton({ selected: false, isApp: meta?.showNavbarHome })} onClick={toggleOpened}>
            {opened ? <CloseIcon /> : <MenuIcon />}
            <Typography>{translate('menu')}</Typography>
          </Box>
        </Stack>
      )}
    </>
  );
};

export default MobileNavigation;
